/**
 * LayoutSwitcher — Modern vs Classic layout mode toggle
 *
 * Reads/writes `designMode` from the global app store (persisted to
 * localStorage under `boox_design_mode`).  Switching is instant: the store
 * sets `data-design-mode` on `<html>` and `useViewTemplate` re-runs its
 * effect synchronously from the in-memory Classic DSL map — no page reload,
 * no network round-trip.
 *
 * Usage:
 *   - In settings: <LayoutSwitcher variant="settings-row" />
 *   - In topbar:   <LayoutSwitcher />
 */

import { Monitor, Building2 } from 'lucide-react';
import { useAppStore } from '@/stores/app-store';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';

interface LayoutSwitcherProps {
  /** 'icon' renders a compact button for topbar use (default).
   *  'settings-row' renders a full labeled row for use inside a settings section. */
  variant?: 'icon' | 'settings-row';
  className?: string;
}

export function LayoutSwitcher({ variant = 'icon', className }: LayoutSwitcherProps) {
  const { designMode, setDesignMode } = useAppStore();
  const { t } = useTranslation();

  const isClassic = designMode === 'classic';

  function toggle() {
    setDesignMode(isClassic ? 'modern' : 'classic');
  }

  if (variant === 'settings-row') {
    return (
      <div className={cn('flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-3', className)}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
            {isClassic ? (
              <Building2 className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Monitor className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-0.5 min-w-0">
            <div className="text-sm font-medium">
              {isClassic
                ? t('settings.layoutClassic', 'Classic Layout')
                : t('settings.layoutModern', 'Modern Layout')}
            </div>
            <div className="text-xs text-muted-foreground">
              {isClassic
                ? t('settings.layoutClassicDesc', 'Compact 2-column view matching the legacy system — familiar field positions, no side panels.')
                : t('settings.layoutModernDesc', 'Full-featured DSL layout with assist panel, right dock, and column sets.')}
            </div>
          </div>
        </div>
        <Switch
          checked={!isClassic}
          onCheckedChange={toggle}
          aria-label={t('settings.layoutModeToggle', 'Toggle layout mode')}
        />
      </div>
    );
  }

  // icon variant — compact topbar button
  return (
    <button
      onClick={toggle}
      title={isClassic
        ? t('settings.switchToModern', 'Switch to Modern layout')
        : t('settings.switchToClassic', 'Switch to Classic layout')}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        isClassic && 'bg-muted text-foreground',
        className,
      )}
    >
      {isClassic ? (
        <Building2 className="h-4 w-4" />
      ) : (
        <Monitor className="h-4 w-4" />
      )}
    </button>
  );
}
